/**
 * Project Detail Page Component
 * 
 * Shows full project details with term sheets, weather data, and results.
 * Allows editing project, adding/removing data, and running calculations.
 */

import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  ArrowLeft, Cloud, BarChart3, Plus,
  Loader2, Edit2, Save,
  Calculator, FolderOpen
} from "lucide-react";
import QuickFlow from "@/components/ProjectDetails/Stepper";

const quickFlowKey = "q";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(projectId !== quickFlowKey);
  const [project, setProject] = useState(null);
  const [termSheets, setTermSheets] = useState([]);
  const [weatherData, setWeatherData] = useState([]);
  const [mergedData, setMergedData] = useState(null);
  const [results, setResults] = useState([]);

  // Available data for adding
  // const [availableTermSheets, setAvailableTermSheets] = useState([]);
  const [availableWeather, setAvailableWeather] = useState([]);
  const [addWeatherDialog, setAddWeatherDialog] = useState(false);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    if(path.includes("/upload")) {
      setStep(0);
    } else if (path.includes("/review")) {
      setStep(1);
    } else if (path.includes("/weather")) {
      setStep(2);
    } else if (path.includes("/burning-cost")) {
      setStep(3);
    } else if (path.includes("/results")) {
      setStep(4);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (projectId !== quickFlowKey) {
      fetchProjectData();
      fetchAvailableData();
    }
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects/${projectId}`);
      setProject(response.data.project);
      setTermSheets(response.data.term_sheets || []);
      setWeatherData(response.data.weather_data || []);
      setMergedData(response.data.merged_data);
      setResults(response.data.results || []);
      setEditName(response.data.project?.name || "");
    } catch (e) {
      console.error('Error fetching project:', e);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableData = async () => {
    try {
      // const [tsResponse, wdResponse] = await Promise.all([
      //   axios.get(`${API}/termsheets?project_id=${projectId}`),
      //   axios.get(`${API}/weather-uploads`)
      // ]);
      // setAvailableTermSheets(tsResponse.data.term_sheets?.filter(ts => ts.status === 'parsed' && !ts.is_merged) || []);
      // setAvailableWeather(wdResponse.data.weather_uploads || []);

      const wdResponse = await axios.get(`${API}/weather-uploads?project_id=${projectId}`);
      setAvailableWeather(wdResponse.data.weather_uploads || []);
    } catch (e) {
      console.error('Error fetching available data:', e);
    }
  };

  const addWeather = async (wdId) => {
    try {
      await axios.post(`${API}/projects/${projectId}/add-weather?weather_id=${wdId}`);
      toast.success('Weather data added to project');
      setAddWeatherDialog(false);
      fetchProjectData();
    } catch (e) {
      toast.error('Failed to add weather data');
    }
  };

  // const removeWeather = async (wdId) => {
  //   try {
  //     await axios.post(`${API}/projects/${projectId}/remove-weather?weather_id=${wdId}`);
  //     toast.success('Weather data removed');
  //     fetchProjectData();
  //   } catch (e) {
  //     toast.error('Failed to remove weather data');
  //   }
  // };

  // const mergeTermSheets = async () => {
  //   if (termSheets.length < 1) {
  //     toast.error('Add at least one term sheet first');
  //     return;
  //   }

  //   try {
  //     const response = await axios.post(`${API}/projects/${projectId}/merge`);
  //     toast.success(`Merged ${termSheets.length} term sheets!`);
  //     fetchProjectData();
  //   } catch (e) {
  //     toast.error('Merge failed: ' + (e.response?.data?.detail || e.message));
  //   }
  // };

  const saveProjectName = async () => {
    if (!editName.trim()) {
      toast.error('Project name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}`, { name: editName });
      toast.success('Project name updated');
      setEditMode(false);
      fetchProjectData();
    } catch (e) {
      toast.error('Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stepChangeHandler = (newStep) => {
    if(newStep < step) {
      navigate(newStep - step);
    }
    setStep(newStep);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-6xl mx-auto bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-heading font-bold text-xl mb-2">Project Not Found</h2>
            <Button onClick={() => navigate('/RWBCIS')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Projects
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const addableWeather = availableWeather.filter(
    wd => !weatherData.find(w => w.id === wd.id)
  );

  return (
    <div className="bg-background">
      {/* Header */}
      <header className="glass-header max-w-6xl mx-auto sticky top-0 z-50 py-4 px-4 lg:px-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/RWBCIS')}
              className="hover:bg-primary/80"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              {editMode ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-8 w-64"
                  />
                  <Button size="sm" onClick={saveProjectName} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </Button>
                </div>
              ) : (
                <h1 className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
                  {project.name}
                  <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-primary/80" onClick={() => setEditMode(true)}>
                    <Edit2 className="h-3 w-3" />
                  </Button>
                </h1>
              )}
              <div className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                {project.state && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-medium">
                    {project.state}
                  </span>
                )}
                {project.season && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">
                    {project.season}
                  </span>
                )}
                {project.tender_year && (
                  <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200 text-xs font-medium">
                    Tender {project.tender_year}
                  </span>
                )}
                {project.termsheet_geo_level && (
                  <span
                    title={`Term sheets are parsed at ${project.termsheet_geo_level} level (below district)`}
                    className="px-2 py-0.5 rounded-full bg-violet-50 text-violet-800 border border-violet-200 text-xs font-medium">
                    {project.termsheet_geo_level}-wise
                  </span>
                )}
                <span>Created {formatDate(project.created_date)}{project.description && ` • ${project.description}`}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {mergedData && weatherData.length > 0 && (
              <Button
                onClick={() => navigate(`/weather/${mergedData.id}`)}
                variant="outline"
              >
                <Calculator className="h-4 w-4 mr-2" />
                Calculate
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto">
        {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6"> */}
        <QuickFlow onChange={stepChangeHandler} />
        {/* </div> */}
        <Outlet />


        {/* Results Section */}
        {results.length > 0 && (
          <Card className="mt-6 border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-600" />
                Calculation Results
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                {results.length} result(s) available
              </p>
              <Button
                className="mt-4"
                onClick={() => navigate(`/results/${mergedData?.id || termSheets[0]?.id}`)}
              >
                View Results
              </Button>
            </CardContent>
          </Card>
        )}
      </main>



      {/* Add Weather Dialog */}
      <Dialog open={addWeatherDialog} onOpenChange={setAddWeatherDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Weather Data to Project</DialogTitle>
            <DialogDescription>
              Select weather data to add to this project.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[300px] overflow-y-auto">
            {addableWeather.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No available weather data</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setAddWeatherDialog(false);
                    navigate('/upload');
                  }}
                >
                  Upload New
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {addableWeather.map((wd) => (
                  <div
                    key={wd.id}
                    onClick={() => addWeather(wd.id)}
                    className="flex items-center justify-between p-3 border border-border rounded-lg cursor-pointer hover:bg-muted"
                  >
                    <div className="flex items-center gap-3">
                      <Cloud className="h-5 w-5 text-blue-500" />
                      <div>
                        <span className="text-sm">{wd.filename}</span>
                        <p className="text-xs text-muted-foreground">
                          {wd.data_summary?.total_records || '?'} records
                        </p>
                      </div>
                    </div>
                    <Plus className="h-4 w-4 text-blue-600" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetailPage;
